import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { searchAllListingTypes } from '../../api/globalSearchAPI';
import { formatItemPriceEgp } from '../../utils/formatPrice';
import { resolveListingBrand } from '../../components/items/ListingDetailShared';
import SEO from '../../components/seo/SEO';

const SHOP_LISTING_KINDS = new Set(['designer_item', 'boutique_item', 'artist_item']);

const shouldShowListingCondition = (item) =>
  !SHOP_LISTING_KINDS.has(item.listingKind);

const Page = styled.div`
  background: #f5ecd7;
  min-height: calc(100vh - 120px);
  padding: 1.5rem 1.25rem 2.5rem;

  @media (max-width: 768px) {
    padding: 1rem 0.85rem 1.75rem;
  }
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Heading = styled.h1`
  margin: 0 0 0.35rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: #1b4332;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Sub = styled.p`
  margin: 0 0 1.25rem;
  color: #3d5a4a;
  font-size: 0.95rem;

  @media (max-width: 768px) {
    margin-bottom: 0.9rem;
    font-size: 0.82rem;
    line-height: 1.35;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.85rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.55rem;
  }
`;

const Card = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  background: transparent;
  border: none;
  overflow: hidden;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.92;
  }
`;

const Thumb = styled.div`
  aspect-ratio: 1;
  background: var(--bg-secondary);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: scale(1.1);
  }
`;

const Meta = styled.div`
  padding: 0.45rem 0.1rem 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  @media (max-width: 768px) {
    padding: 0.35rem 0.05rem 0.15rem;
  }
`;

const Price = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: #1b4332;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const BrandMeta = styled.div`
  font-size: 0.72rem;
  color: #6b7c72;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;

  @media (max-width: 768px) {
    font-size: 0.66rem;
  }
`;

const Brand = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const Empty = styled.p`
  text-align: center;
  color: #6b7c72;
  padding: 2rem 1rem;
`;

const SearchResultsPage = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const query = (params.get('q') || params.get('search') || '').trim();
  const category = (params.get('category') || '').trim();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(Boolean(query || category));
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!query && !category) {
      setResults([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);
    searchAllListingTypes(query, { category })
      .then((rows) => {
        if (!cancelled) setResults(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setResults([]);
          setError(t('search.loadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query, category, t]);

  const headingLabel = query
    ? (category
      ? t('search.resultsForInCategory', { query, category })
      : t('search.resultsFor', { query }))
    : (category
      ? t('search.resultsInCategory', { category })
      : t('search.emptyQuery'));

  return (
    <Page>
      <SEO
        title="Search Results | Flea Marketplace"
        description="Search Flea Marketplace listings."
        noindex
      />
      <Inner>
        {(query || category) ? (
          <>
            <Heading>{headingLabel}</Heading>
            <Sub>
              {loading
                ? t('browse.loadingItems')
                : t('search.resultCount', { count: results.length })}
            </Sub>
          </>
        ) : (
          <Heading>{t('search.emptyQuery')}</Heading>
        )}

        {error && <Empty>{error}</Empty>}

        {!loading && !error && (query || category) && results.length === 0 && (
          <Empty>
            {query
              ? t('search.noResults', { query })
              : t('search.noResultsInCategory', { category })}
          </Empty>
        )}

        {!loading && results.length > 0 && (
          <Grid>
            {results.map((item) => (
              <Card
                key={item.key}
                to={item.href}
                state={item.linkState}
              >
                <Thumb>
                  {item.image ? <img src={item.image} alt="" /> : null}
                </Thumb>
                <Meta>
                  <Price>{formatItemPriceEgp(item.price)}</Price>
                  <BrandMeta>
                    <Brand>{resolveListingBrand(item.raw || item) || '—'}</Brand>
                    {shouldShowListingCondition(item) && (item.raw?.condition || item.condition) ? (
                      <span>· {item.raw?.condition || item.condition}</span>
                    ) : null}
                  </BrandMeta>
                </Meta>
              </Card>
            ))}
          </Grid>
        )}
      </Inner>
    </Page>
  );
};

export default SearchResultsPage;
